import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { HostingService } from '../../services/hosting.service';
import { SearchService, SearchData } from '../../services/search.service';
import { Hosting } from '../../models/HostingModel';
import { Subject, debounceTime, takeUntil, filter } from 'rxjs';
import { RouterLink } from '@angular/router';
import { NavbarComponent } from '../../components/navbar/navbar.component';
import { FooterComponent } from '../../components/footer/footer.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

interface ApiResponse {
  items: Hosting[];
  lastKey?: string;
  hasMore: boolean;
}

interface SearchCriteria {
  checkIn: string;
  checkOut: string;
  adults: number;
  children: number;
  babies: number;
  pets: number;
}

@Component({
  selector: 'app-hostinglist',
  imports: [
    RouterLink,
    CommonModule,
    NavbarComponent,
    FooterComponent,
    FormsModule
  ],
  templateUrl: './hostinglist.component.html',
  styleUrls: ['./hostinglist.component.css']
})
export class HostinglistComponent implements OnInit, OnDestroy {
  hostings: Hosting[] = [];
  isLoading = false;
  lastKey?: string;
  hasMore = true;
  limit = 8; // Items por carga

  // Propiedades para el formulario de búsqueda
  searchCriteria: SearchCriteria = {
    checkIn: '',
    checkOut: '',
    adults: 0, // 🔄 Cambio: Empezar sin huéspedes seleccionados
    children: 0,
    babies: 0,
    pets: 0
  };
  showGuestsDropdown = false;

  private destroy$ = new Subject<void>();
  private scrollDebouncer$ = new Subject<void>();

  constructor(
    private hostingService: HostingService,
    private searchService: SearchService
  ) {}

  ngOnInit(): void {
    this.initializeSearchDates(); // Siempre empezar con fechas por defecto
    // Comentado: this.loadSavedSearchData(); // No cargar automáticamente datos guardados
    this.setupScrollListener();
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeSearchDates(): void {
    // 🔄 Cambio: Inicializar fechas en blanco para nueva experiencia de usuario
    this.searchCriteria.checkIn = '';
    this.searchCriteria.checkOut = '';
    
    // Si necesitas fechas por defecto, descomenta las siguientes líneas:
    // const today = new Date();
    // const tomorrow = new Date(today);
    // tomorrow.setDate(tomorrow.getDate() + 1);
    // this.searchCriteria.checkIn = today.toISOString().split('T')[0];
    // this.searchCriteria.checkOut = tomorrow.toISOString().split('T')[0];
  }

  private loadSavedSearchData(): void {
    const savedData = this.searchService.getSearchData();
    if (savedData) {
      this.searchCriteria = {
        checkIn: savedData.checkIn,
        checkOut: savedData.checkOut,
        adults: savedData.adults,
        children: savedData.children,
        babies: savedData.babies,
        pets: savedData.pets
      };
    }
  }

  // 🆕 Método para cargar datos guardados solo cuando sea necesario
  public loadSavedSearchDataIfNeeded(): void {
    // Solo cargar si hay datos válidos y útiles
    const savedData = this.searchService.getSearchData();
    if (savedData?.hotelId) {
      // Solo cargar si hay información de hotel (viene de otra página)
      this.loadSavedSearchData();
    }
  }

  private setupScrollListener(): void {
    this.scrollDebouncer$.pipe(
      debounceTime(300),
      filter(() => !this.isLoading && this.hasMore),
      takeUntil(this.destroy$)
    ).subscribe(() => this.loadMoreData());
  }

  @HostListener('window:scroll')
  onScroll(): void {
    const threshold = 200; // Pixeles antes del final
    const position = window.scrollY + window.innerHeight;
    const height = document.body.scrollHeight;

    if (position > height - threshold) {
      this.scrollDebouncer$.next();
    }
  }

  private loadInitialData(): void {
    this.isLoading = true;
    this.hostingService.getHostings(this.limit)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => this.handleResponse(response),
        error: (err) => this.handleError(err)
      });
  }

  private loadMoreData(): void {
    if (!this.hasMore || this.isLoading) return;

    this.isLoading = true;
    this.hostingService.getHostings(this.limit, this.lastKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => this.handleResponse(response),
        error: (err) => this.handleError(err)
      });
  }

  private handleResponse(response: ApiResponse): void {
    this.hostings = [...this.hostings, ...response.items];
    this.lastKey = response.lastKey;
    this.hasMore = response.hasMore;
    this.isLoading = false;
  }

  private handleError(error: any): void {
    console.error('Error loading hostings:', error);
    this.isLoading = false;
  }

  trackByDuplicated(index: number, item: any): string {
    return `${item.id}_${index}`; // Combina ID con posición única
  }

  // Métodos para el formulario de búsqueda
  toggleGuestsDropdown(): void {
    this.showGuestsDropdown = !this.showGuestsDropdown;
  }

  updateGuests(type: 'adults' | 'children' | 'babies' | 'pets', change: number): void {
    const newValue = this.searchCriteria[type] + change;
    
    if (type === 'adults' && newValue >= 1) {
      this.searchCriteria.adults = newValue;
    } else if (type === 'children' && newValue >= 0) {
      this.searchCriteria.children = newValue;
    } else if (type === 'babies' && newValue >= 0) {
      this.searchCriteria.babies = newValue;
    } else if (type === 'pets' && newValue >= 0) {
      this.searchCriteria.pets = newValue;
    }
  }

  getGuestsSummary(): string {
    const parts: string[] = [];
    
    if (this.searchCriteria.adults > 0) {
      parts.push(`${this.searchCriteria.adults} adulto${this.searchCriteria.adults > 1 ? 's' : ''}`);
    }
    
    if (this.searchCriteria.children > 0) {
      parts.push(`${this.searchCriteria.children} niño${this.searchCriteria.children > 1 ? 's' : ''}`);
    }
    
    if (this.searchCriteria.babies > 0) {
      parts.push(`${this.searchCriteria.babies} bebé${this.searchCriteria.babies > 1 ? 's' : ''}`);
    }
    
    if (this.searchCriteria.pets > 0) {
      parts.push(`${this.searchCriteria.pets} mascota${this.searchCriteria.pets > 1 ? 's' : ''}`);
    }
    
    return parts.length > 0 ? parts.join(' · ') : 'Huéspedes'; // 🔄 Cambio: Mostrar texto genérico
  }

  onSearch(): void {
    console.log('Criterios de búsqueda:', this.searchCriteria);
    
    // Cerrar dropdown si está abierto
    this.showGuestsDropdown = false;
    
    // Validar fechas
    if (!this.searchCriteria.checkIn || !this.searchCriteria.checkOut) {
      alert('Por favor selecciona las fechas de entrada y salida');
      return;
    }
    
    // 🆕 Validar huéspedes
    if (this.searchCriteria.adults <= 0) {
      alert('Debe haber al menos 1 adulto para realizar la búsqueda');
      return;
    }
    
    const checkInDate = new Date(this.searchCriteria.checkIn);
    const checkOutDate = new Date(this.searchCriteria.checkOut);
    
    if (checkOutDate <= checkInDate) {
      alert('La fecha de salida debe ser posterior a la fecha de entrada');
      return;
    }
    
    // Guardar datos de búsqueda en el servicio
    this.searchService.setSearchData({
      checkIn: this.searchCriteria.checkIn,
      checkOut: this.searchCriteria.checkOut,
      adults: this.searchCriteria.adults,
      children: this.searchCriteria.children,
      babies: this.searchCriteria.babies,
      pets: this.searchCriteria.pets
    });
    
    // 🆕 NUEVA FUNCIONALIDAD: Filtrar habitaciones disponibles
    this.searchAvailableRooms();
  }

  // 🆕 Método para buscar habitaciones disponibles
  async searchAvailableRooms(): Promise<void> {
    this.isLoading = true;
    
    try {
      console.log('🔍 Iniciando búsqueda de habitaciones disponibles...');
      
      // Filtrar hostings disponibles usando el nuevo método del servicio
      const availableHostings = await this.hostingService.getAvailableHostings(
        this.searchCriteria.checkIn,
        this.searchCriteria.checkOut
      );
      
      // Reemplazar la lista actual con los resultados filtrados
      this.hostings = availableHostings;
      
      // Actualizar estado de paginación
      this.hasMore = false; // Por ahora desactivamos paginación en búsqueda
      this.lastKey = undefined;
      
      const numberOfNights = Math.ceil((new Date(this.searchCriteria.checkOut).getTime() - new Date(this.searchCriteria.checkIn).getTime()) / (1000 * 3600 * 24));
      
      // Mostrar mensaje de resultado
      if (availableHostings.length > 0) {
        alert(`✅ Búsqueda completada!
📅 ${numberOfNights} noche${numberOfNights > 1 ? 's' : ''}
👥 ${this.getGuestsSummary()}
🏨 ${availableHostings.length} alojamiento${availableHostings.length > 1 ? 's' : ''} disponible${availableHostings.length > 1 ? 's' : ''}

Los resultados muestran solo alojamientos con habitaciones disponibles en las fechas seleccionadas.`);
      } else {
        alert(`😔 No hay habitaciones disponibles
📅 Para las fechas: ${this.searchCriteria.checkIn} - ${this.searchCriteria.checkOut}
👥 ${this.getGuestsSummary()}

Por favor intenta con otras fechas o reduce el número de huéspedes.`);
      }
      
    } catch (error) {
      console.error('❌ Error en la búsqueda:', error);
      alert('⚠️ Error al buscar habitaciones disponibles. Mostrando todos los alojamientos como respaldo.');
      
      // Fallback: cargar todos los hostings
      this.resetToAllHostings();
    } finally {
      this.isLoading = false;
    }
  }

  // 🆕 Método para resetear a mostrar todos los hostings
  private resetToAllHostings(): void {
    // Resetear estado de paginación
    this.hostings = [];
    this.lastKey = undefined;
    this.hasMore = true;
    
    // Cargar datos iniciales
    this.loadMoreData();
  }

  // 🆕 Método público para ver todos los alojamientos
  viewAllHostings(): void {
    console.log('🏨 Mostrando todos los alojamientos...');
    this.resetToAllHostings();
    alert('✅ Mostrando todos los alojamientos disponibles.\n\nSe ha removido el filtro de fechas.');
  }
}